/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module AviComponentRenderingModule */

import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    Type,
} from '@angular/core';

import {
    AviDropdownButtonPosition,
    IAviDropdownButtonAction,
} from 'ng/shared/components/avi-dropdown-button';

import './avi-component-adder.component.less';

/**
 * Used for displaying options in the dropdown button.
 */
export interface IComponentAddOption {
    label: string;
}

/**
 * Used to render a component.
 */
export interface ISelectedComponent {
    component: Type<Component>;
    componentBindings?: Record<string, unknown>;
}

/**
 * @description
 *     Simple component that shows a dropdown button and a list of rendered components underneath. The user can select
 *     an option from the dropdown button which leads to a component being added to the list.
 * @author alextsg
 */
@Component({
    selector: 'avi-component-adder',
    templateUrl: './avi-component-adder.component.html',
})
export class AviComponentAdderComponent<T extends IComponentAddOption> implements OnInit, OnChanges {
    /**
     * List of options that the user can add. These options will be used to create the dropdown button options.
     */
    @Input()
    public options: T[] = [];

    /**
     * List of components that should be rendered below the dropdown button.
     */
    @Input()
    public selectedComponents: ISelectedComponent[] = [];

    /**
     * True to disable the dropdown button.
     */
    @Input()
    public disableAdd = false;

    /**
     * String displayed in the dropdown button if no other options can be added.
     */
    @Input()
    public noAvailableOptionsLabel = '';

    /**
     * String displayed when no components have been rendered.
     */
    @Input()
    public noSelectedComponentsLabel = '';

    /**
     * String displayed in the dropdown button.
     */
    @Input()
    public addLabel = '';

    /**
     * An index binding gets passed to rendered components to ensure uniqueness. If the rendered component uses a
     * different binding name for a unique index property, that can be passed here.
     */
    @Input()
    public indexAlias ?: string;

    @Output()
    public onAdd = new EventEmitter<T>();

    public readonly actionPosition = AviDropdownButtonPosition.BOTTOM_LEFT;

    public addDropdownActions: IAviDropdownButtonAction[] = [];

    /** @override */
    public ngOnInit(): void {
        this.setDropdownButtonActions();
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        const { options } = changes;

        if (options && !options.firstChange) {
            this.setDropdownButtonActions();
        }
    }

    public trackByItem(index: number, item: ISelectedComponent): ISelectedComponent {
        return item;
    }

    /**
     * Set the AviDropdownButton options. If no options are available to select, a disabled option is shown.
     */
    private setDropdownButtonActions(): void {
        this.addDropdownActions = this.options.map(option => ({
            label: option.label,
            onClick: () => this.onAdd.emit(option),
        }));

        if (!this.addDropdownActions.length) {
            this.addDropdownActions = [{
                label: this.noAvailableOptionsLabel,
                onClick: () => {},
                disabled: () => true,
            }];
        }
    }
}
